import { type ReactNode, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import { ExternalLink, Linkedin, Mail, Phone } from "lucide-react";
import {
  type ColumnDef,
  type RowSelectionState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MotionTableRow } from "@/components/ui/motion";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { formatDate, formatRelativeDate, initials } from "@/lib/formatters";
import { getEnrichmentColor } from "@/lib/constants";
import {
  COPIED_FROM_COMPANY_LABEL,
  formatPhone,
  isPhoneNumber,
  phoneCopiedFromCompany,
  phoneForCopy,
  phoneHref,
} from "@/lib/phone";
import { CopyButton } from "@/components/shared/CopyButton";
import { HiddenDetail, isHidden } from "@/components/shared/HiddenDetail";
import type { AvailableColumn, Company, Contact } from "@/api/types";

interface ContactTableProps {
  items: Contact[];
  companyById: Map<number, Company>;
  onRowClick: (contact: Contact) => void;
  selectedIds?: Set<number>;
  onSelectionChange?: (ids: Set<number>) => void;
  selectable?: boolean;
  /** Ordered list of column keys (from CONTACT_COLUMNS) to render
   *  between the select checkbox column and any fixed trailing columns.
   *  When omitted the table renders its legacy hardcoded order. */
  visibleColumnKeys?: string[];
  /** Catalog used to resolve labels and types for keys that don't have
   *  a dedicated rich renderer. Required when visibleColumnKeys
   *  references a non-canonical key. */
  availableColumns?: AvailableColumn[];
}

export function ContactTable({
  items,
  companyById,
  onRowClick,
  selectedIds,
  onSelectionChange,
  selectable = false,
  visibleColumnKeys,
  availableColumns,
}: ContactTableProps) {
  const rowSelection = useMemo<RowSelectionState>(() => {
    const out: RowSelectionState = {};
    if (!selectedIds) return out;
    for (const id of selectedIds) out[String(id)] = true;
    return out;
  }, [selectedIds]);

  const columns = useMemo<ColumnDef<Contact>[]>(() => {
    const cols: ColumnDef<Contact>[] = [];
    if (selectable) {
      cols.push({
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                  ? "indeterminate"
                  : false
            }
            onCheckedChange={(value) =>
              table.toggleAllRowsSelected(value === true)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(value === true)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        size: 36,
      });
    }
    cols.push(
      {
        id: "contact",
        accessorKey: "full_name",
        header: "Contact",
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8 border border-slate-200">
                <AvatarFallback className="bg-indigo-100 text-[11px] font-semibold text-indigo-700">
                  {initials(c.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="truncate font-medium text-slate-900">
                  {c.full_name}
                </div>
                {c.title && (
                  <div className="truncate text-xs text-slate-500">
                    {c.title}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: "company",
        accessorFn: (row) => row.company_id,
        header: "Company",
        cell: ({ row }) => {
          const company = companyById.get(row.original.company_id);
          if (!company) {
            return <span className="text-xs text-slate-400">—</span>;
          }
          return (
            <span className="truncate text-sm text-slate-700">
              {company.name}
            </span>
          );
        },
      },
      {
        id: "email",
        accessorKey: "email",
        header: "Email",
        cell: ({ getValue, row }) => {
          const email = getValue<string | null>();
          if (isHidden(row.original, "email")) return <HiddenDetail label="Email" className="text-xs" />;
          if (!email) return <span className="text-xs text-slate-400">—</span>;
          return (
            <span className="group/copy inline-flex max-w-full items-center gap-0.5">
              <a
                href={`mailto:${email}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex max-w-[220px] items-center gap-1.5 truncate text-xs text-indigo-600 hover:text-indigo-500"
              >
                <Mail className="h-3 w-3 shrink-0" />
                <span className="truncate">{email}</span>
              </a>
              <CopyButton value={email} label="email" reveal="hover" />
            </span>
          );
        },
      },
      {
        id: "phone",
        accessorKey: "phone",
        header: "Phone",
        cell: ({ getValue, row }) => {
          const phone = getValue<string | null>();
          if (isHidden(row.original, "phone")) return <HiddenDetail label="Phone" className="text-xs" />;
          if (!isPhoneNumber(phone)) return <span className="text-xs text-slate-400">—</span>;
          return (
            <span
              className="group/copy inline-flex items-center gap-0.5"
              title={phoneCopiedFromCompany(row.original) ? COPIED_FROM_COMPANY_LABEL : undefined}
            >
              <a
                href={phoneHref(phone) ?? undefined}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900"
              >
                <Phone className="h-3 w-3 shrink-0" />
                {formatPhone(phone)}
              </a>
              <CopyButton value={phoneForCopy(phone)} label="phone number" reveal="hover" />
            </span>
          );
        },
      },
      {
        id: "linkedin",
        accessorKey: "linkedin_url",
        header: "LinkedIn",
        cell: ({ getValue }) => {
          const url = getValue<string | null>();
          if (!url) return <span className="text-xs text-slate-400">—</span>;
          return (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
            >
              <Linkedin className="h-3 w-3" />
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          );
        },
      },
      {
        id: "decision_maker",
        accessorKey: "is_decision_maker",
        header: "DM",
        cell: ({ getValue }) => {
          const dm = getValue<boolean>();
          if (!dm) return <span className="text-xs text-slate-300">—</span>;
          return (
            <span className="inline-flex items-center rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
              DM
            </span>
          );
        },
      },
      {
        id: "enrichment",
        accessorKey: "enrichment_status",
        header: "Enrichment",
        cell: ({ getValue }) => {
          const status = getValue<string>();
          const colors = getEnrichmentColor(status);
          return (
            <span
              className={cn(
                "inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium capitalize",
                colors.bg,
                colors.text,
                colors.border,
              )}
            >
              {status}
            </span>
          );
        },
      },
      {
        id: "source",
        accessorKey: "source",
        header: "Source",
        cell: ({ getValue }) => (
          <span className="text-xs capitalize text-slate-500">
            {getValue<string>()}
          </span>
        ),
      },
      {
        id: "updated",
        accessorKey: "updated_at",
        header: "Updated",
        cell: ({ getValue }) => (
          <span className="text-xs text-slate-400">
            {formatRelativeDate(getValue<string>())}
          </span>
        ),
      },
    );
    return cols;
  }, [companyById, selectable]);

  // When the parent supplies a visibleColumnKeys list (from CONTACT_COLUMNS
  // + user prefs), filter and reorder the columns built above to match.
  // The select column (if selectable) always stays first. Catalog keys
  // without a dedicated rich renderer fall through to a generic
  // ColumnDef driven by the AvailableColumn config.
  const finalColumns = useMemo<ColumnDef<Contact>[]>(() => {
    if (!visibleColumnKeys) return columns;
    const byKey = new Map<string, ColumnDef<Contact>>();
    for (const c of columns) {
      const id = c.id;
      if (!id) continue;
      // Register every existing column under its own id AND under any
      // backend-key aliases so the catalog matches whichever spelling
      // it uses.
      byKey.set(id, c);
      switch (id) {
        case "contact":
          byKey.set("full_name", c);
          break;
        case "linkedin":
          byKey.set("linkedin_url", c);
          break;
        case "decision_maker":
          byKey.set("is_decision_maker", c);
          break;
        case "enrichment":
          byKey.set("enrichment_status", c);
          break;
        case "updated":
          byKey.set("updated_at", c);
          break;
      }
    }
    const out: ColumnDef<Contact>[] = [];
    const selectCol = columns.find((c) => c.id === "select");
    if (selectCol) out.push(selectCol);
    const seen = new Set<string>(["select"]);
    for (const key of visibleColumnKeys) {
      if (seen.has(key)) continue;
      seen.add(key);
      const c = byKey.get(key);
      if (c) {
        out.push(c);
        continue;
      }
      const fallback = buildGenericContactColumn(key, availableColumns);
      if (fallback) out.push(fallback);
    }
    return out;
  }, [columns, visibleColumnKeys, availableColumns]);

  const table = useReactTable({
    data: items,
    columns: finalColumns,
    state: selectable ? { rowSelection } : undefined,
    enableRowSelection: selectable,
    getRowId: (row) => String(row.id),
    onRowSelectionChange: (updater) => {
      if (!selectable || !onSelectionChange) return;
      const next =
        typeof updater === "function" ? updater(rowSelection) : updater;
      const ids = new Set<number>();
      for (const [id, selected] of Object.entries(next)) {
        if (selected) ids.add(Number(id));
      }
      onSelectionChange(ids);
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-x-auto rounded-plane bg-white shadow-plane">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="border-slate-900/[0.06] hover:bg-transparent">
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{
                    width: header.column.columnDef.size
                      ? `${header.column.columnDef.size}px`
                      : undefined,
                  }}
                  className="text-[11px] font-medium uppercase tracking-wider text-slate-500"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          <AnimatePresence initial={false} mode="popLayout">
            {table.getRowModel().rows.map((row, idx) => (
              <MotionTableRow
                key={row.id}
                index={idx}
                data-state={row.getIsSelected() ? "selected" : undefined}
                onClick={() => onRowClick(row.original)}
                className={cn(
                  "cursor-pointer border-slate-900/[0.06] transition-colors duration-150 hover:bg-indigo-500/[0.04] motion-reduce:transition-none",
                  row.getIsSelected() && "bg-indigo-500/[0.07] hover:bg-indigo-500/[0.1]",
                )}
              >
                {row.getVisibleCells().map((cell) => {
                  // The select column owns the entire cell's hit area —
                  // see CompanyTable for the same fix.
                  const isSelectCell = cell.column.id === "select";
                  return (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        "py-3 text-sm",
                        isSelectCell && "cursor-pointer",
                      )}
                      onClick={
                        isSelectCell
                          ? (e) => {
                              e.stopPropagation();
                              row.toggleSelected(!row.getIsSelected());
                            }
                          : undefined
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </MotionTableRow>
            ))}
          </AnimatePresence>
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Renders the same Table DOM as ContactTable so column widths and row
 * heights line up between loading and loaded states.
 */
/**
 * Generic ColumnDef factory for backend-supplied catalog keys that
 * don't have a dedicated rich renderer above (e.g. "title",
 * "created_at"). Reads the value directly off Contact and formats by
 * the catalog's `type`.
 */
function buildGenericContactColumn(
  key: string,
  availableColumns: AvailableColumn[] | undefined,
): ColumnDef<Contact> | null {
  const cfg = availableColumns?.find((c) => c.key === key);
  if (!cfg) return null;
  return {
    id: cfg.key,
    header: cfg.label,
    accessorFn: (row) => (row as unknown as Record<string, unknown>)[cfg.key],
    cell: ({ getValue }) => renderGenericContactCell(getValue(), cfg.type),
    enableSorting: false,
  };
}

function renderGenericContactCell(value: unknown, type: string): ReactNode {
  if (value == null || value === "") {
    return <span className="text-xs text-slate-400">—</span>;
  }
  switch (type) {
    case "date":
      return (
        <span className="text-xs text-slate-500">
          {typeof value === "string" ? formatDate(value) : String(value)}
        </span>
      );
    case "badge":
      return (
        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium capitalize text-slate-600">
          {String(value).replace(/_/g, " ")}
        </span>
      );
    default:
      return (
        <span className="block max-w-[220px] truncate text-sm text-slate-700">
          {String(value)}
        </span>
      );
  }
}

export function ContactTableSkeleton({
  rows = 8,
  selectable = false,
}: {
  rows?: number;
  selectable?: boolean;
}) {
  return (
    <div className="overflow-x-auto rounded-plane bg-white shadow-plane">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-900/[0.06] hover:bg-transparent">
            {selectable && (
              <TableHead style={{ width: 36 }} className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                <Skeleton className="h-4 w-4" />
              </TableHead>
            )}
            <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Contact
            </TableHead>
            <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Company
            </TableHead>
            <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Email
            </TableHead>
            <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Phone
            </TableHead>
            <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Status
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }).map((_, i) => (
            <TableRow key={i} className="border-slate-900/[0.06]">
              {selectable && (
                <TableCell className="py-3">
                  <Skeleton className="h-4 w-4" />
                </TableCell>
              )}
              <TableCell className="py-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-3.5 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              </TableCell>
              <TableCell className="py-3">
                <Skeleton className="h-3.5 w-28" />
              </TableCell>
              <TableCell className="py-3">
                <Skeleton className="h-3.5 w-36" />
              </TableCell>
              <TableCell className="py-3">
                <Skeleton className="h-3.5 w-24" />
              </TableCell>
              <TableCell className="py-3">
                <Skeleton className="h-5 w-20 rounded-full" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
